import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useResource } from '../../hooks/useResource';
import { SensitiveActionDialog } from '../../shared/actions/SensitiveActionDialog';
import { PageState } from '../../shared/data/PageState';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { MutationError, toDialogError } from './PartnerFormDialog';
import { toPageError } from './PartnerListPage';
import type { PartnerRepository } from './repository';
import type { OneTimeSecretResult, PartnerCredential } from './types';

export function CredentialsPanel({
  language,
  partnerId,
  repository,
}: {
  readonly language: TimeLanguage;
  readonly partnerId: string;
  readonly repository: PartnerRepository;
}) {
  const { t } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);
  const [issueOpen, setIssueOpen] = useState(false);
  const [action, setAction] = useState<{
    readonly type: 'rotate' | 'revoke';
    readonly credential: PartnerCredential;
  } | null>(null);
  const [expiryCredential, setExpiryCredential] = useState<PartnerCredential | null>(null);
  const [oneTimeSecret, setOneTimeSecret] = useState<OneTimeSecretResult | null>(null);
  const resource = useResource(
    (signal) => repository.listCredentials(partnerId, signal),
    [repository, partnerId, refreshKey],
  );

  const completed = (secret?: OneTimeSecretResult) => {
    if (secret !== undefined) setOneTimeSecret(secret);
    setRefreshKey((key) => key + 1);
  };

  return (
    <Stack component="section" spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ justifyContent: 'space-between' }}
      >
        <div>
          <Typography component="h2" variant="h5">
            {t('partners.credentials.title')}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {t('partners.credentials.safeMetadata')}
          </Typography>
        </div>
        <Button onClick={() => setIssueOpen(true)} variant="contained">
          {t('partners.credentials.issue')}
        </Button>
      </Stack>
      <PageState
        emptyMessage={t('partners.credentials.empty')}
        error={toPageError(resource.error, t('partners.credentials.loadError'))}
        onRetry={() => setRefreshKey((key) => key + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table aria-label={t('partners.credentials.title')} size="small" sx={{ minWidth: 860 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('partners.credentials.keyId')}</TableCell>
                  <TableCell>{t('partners.fields.status')}</TableCell>
                  <TableCell>{t('partners.credentials.validFrom')}</TableCell>
                  <TableCell>{t('partners.credentials.expiresAt')}</TableCell>
                  <TableCell>{t('partners.credentials.lastUsedAt')}</TableCell>
                  <TableCell>{t('partners.credentials.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resource.data.map((credential) => (
                  <TableRow key={credential.id}>
                    <TableCell>{credential.keyId}</TableCell>
                    <TableCell>
                      <Chip
                        color={
                          credential.status === 'ACTIVE'
                            ? 'success'
                            : credential.status === 'REVOKED'
                              ? 'error'
                              : 'warning'
                        }
                        label={credential.status}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{displayTime(credential.validFrom, language)}</TableCell>
                    <TableCell>{displayTime(credential.expiresAt, language)}</TableCell>
                    <TableCell>{displayTime(credential.lastUsedAt, language)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          onClick={() => setAction({ type: 'rotate', credential })}
                          size="small"
                        >
                          {t('partners.credentials.rotate')}
                        </Button>
                        <Button onClick={() => setExpiryCredential(credential)} size="small">
                          {t('partners.credentials.expiry')}
                        </Button>
                        <Button
                          color="error"
                          disabled={credential.status === 'REVOKED'}
                          onClick={() => setAction({ type: 'revoke', credential })}
                          size="small"
                        >
                          {t('partners.credentials.revoke')}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageState>
      <CredentialIssueDialog
        onClose={() => setIssueOpen(false)}
        onIssued={(secret) => completed(secret)}
        open={issueOpen}
        partnerId={partnerId}
        repository={repository}
      />
      {action === null ? null : (
        <SensitiveActionDialog
          impact={t(`partners.credentials.${action.type}Impact`, {
            keyId: action.credential.keyId,
          })}
          onClose={() => setAction(null)}
          onConfirm={async ({ reason }) => {
            if (action.type === 'rotate') {
              completed(
                await repository.rotateCredential(partnerId, action.credential.id, { reason }),
              );
            } else {
              await repository.revokeCredential(partnerId, action.credential.id, { reason });
              completed();
            }
          }}
          open
          requireEvidence={false}
          showEvidence={false}
          title={t(`partners.credentials.${action.type}Title`)}
        />
      )}
      <CredentialExpiryDialog
        credential={expiryCredential}
        onClose={() => setExpiryCredential(null)}
        onSaved={() => completed()}
        partnerId={partnerId}
        repository={repository}
      />
      <OneTimeSecretDialog onAcknowledged={() => setOneTimeSecret(null)} result={oneTimeSecret} />
    </Stack>
  );
}

function CredentialIssueDialog({
  onClose,
  onIssued,
  open,
  partnerId,
  repository,
}: {
  readonly onClose: () => void;
  readonly onIssued: (secret: OneTimeSecretResult) => void;
  readonly open: boolean;
  readonly partnerId: string;
  readonly repository: PartnerRepository;
}) {
  const { t } = useTranslation();
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<{
    readonly expiresAt: string;
    readonly reason: string;
  }>({ defaultValues: { expiresAt: '', reason: '' } });
  const close = () => {
    if (!submitting.current) {
      reset();
      setError(null);
      onClose();
    }
  };
  const submit = handleSubmit(async (values) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      const expiresAt = values.expiresAt.trim();
      const result = await repository.issueCredential(partnerId, {
        reason: values.reason.trim(),
        ...(expiresAt.length === 0 ? {} : { expiresAt }),
      });
      onIssued(result);
      closeAfterSubmit(reset, onClose);
    } catch (submitError) {
      setError(toDialogError(submitError, t('partners.credentials.mutationError')));
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  });

  return (
    <Dialog fullWidth onClose={close} open={open}>
      <DialogTitle>{t('partners.credentials.issueTitle')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="credential-issue-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">{t('partners.credentials.secretWarning')}</Alert>
          <TextField
            error={errors.expiresAt !== undefined}
            helperText={errors.expiresAt?.message}
            label={t('partners.credentials.expiresAtOptional')}
            placeholder="2027-07-23T00:00:00Z"
            {...register('expiresAt')}
          />
          <TextField
            autoFocus
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('partners.fields.reason')}
            multiline
            {...register('reason', {
              required: t('partners.validation.reasonRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('partners.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="credential-issue-form" loading={busy} type="submit" variant="contained">
          {t('partners.credentials.issueVerb')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CredentialExpiryDialog({
  credential,
  onClose,
  onSaved,
  partnerId,
  repository,
}: {
  readonly credential: PartnerCredential | null;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly partnerId: string;
  readonly repository: PartnerRepository;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const submitting = useRef(false);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<{
    readonly expiresAt: string;
    readonly reason: string;
  }>({ defaultValues: { expiresAt: '', reason: '' } });
  const submit = handleSubmit(async (values) => {
    if (credential === null || submitting.current) return;
    submitting.current = true;
    setError(null);
    try {
      await repository.changeCredentialExpiry(partnerId, credential.id, {
        expiresAt: values.expiresAt.trim(),
        reason: values.reason.trim(),
      });
      onSaved();
      reset();
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('partners.credentials.mutationError')));
    } finally {
      submitting.current = false;
    }
  });
  return (
    <Dialog fullWidth onClose={() => !submitting.current && onClose()} open={credential !== null}>
      <DialogTitle>{t('partners.credentials.expiryTitle')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="credential-expiry-form"
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <TextField
            error={errors.expiresAt !== undefined}
            helperText={errors.expiresAt?.message}
            label={t('partners.credentials.expiresAt')}
            {...register('expiresAt', { required: t('partners.credentials.expiryRequired') })}
          />
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('partners.fields.reason')}
            multiline
            {...register('reason', { required: t('partners.validation.reasonRequired') })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <Button form="credential-expiry-form" type="submit" variant="contained">
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function OneTimeSecretDialog({
  onAcknowledged,
  result,
}: {
  readonly onAcknowledged: () => void;
  readonly result: OneTimeSecretResult | null;
}) {
  const { t } = useTranslation();
  const [discarded, setDiscarded] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const acknowledge = () => {
    setDiscarded(false);
    setCopyError(false);
    onAcknowledged();
  };
  const copy = async () => {
    if (result === null) return;
    try {
      await navigator.clipboard.writeText(result.secret);
      acknowledge();
    } catch {
      setCopyError(true);
    }
  };
  return (
    <Dialog onClose={() => undefined} open={result !== null}>
      <DialogTitle>{t('partners.credentials.oneTimeTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">{t('partners.credentials.oneTimeWarning')}</Alert>
          {copyError ? <Alert severity="error">{t('partners.credentials.copyError')}</Alert> : null}
          <Typography component="code" sx={{ overflowWrap: 'anywhere', userSelect: 'all' }}>
            {result?.secret}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={discarded}
                onChange={(event) => setDiscarded(event.target.checked)}
              />
            }
            label={t('partners.credentials.discarded')}
          />
          {result?.requestId === null || result?.requestId === undefined ? null : (
            <Typography color="text.secondary" variant="caption">
              Request ID: {result.requestId}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void copy()}>{t('partners.credentials.copyClose')}</Button>
        <Button disabled={!discarded} onClick={acknowledge} variant="contained">
          {t('partners.credentials.acknowledgeClose')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function closeAfterSubmit(reset: () => void, onClose: () => void): void {
  reset();
  onClose();
}

function displayTime(value: string | null, language: TimeLanguage): string {
  return value === null ? '—' : formatTimestamp(value, 'Asia/Bangkok', language).display;
}
